import { useTheme } from "@mui/material";
import {
  MenuButtonAddTable,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTaskList,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign,
  isTouchDevice,
  MenuButton,
} from "mui-tiptap";

import { MenuButtonBackgroundColor } from "./MenuButtonBackgroundColor";
import { MenuButtonInlineFields } from "./MenuButtonInlineFields";
import CustomMenuSelectHeading from "./CustomMenuSelectHeading";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";

export default function EditorMenuControls({
  editor,
  isTrackingEnabled = true,
  setIsTrackingEnabled,
  isTrackingHistoryEnabled = true,
  setIsTrackingHistoryEnabled,
}) {
  const theme = useTheme();

  // const getBase64EncodedImage = (file) => {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = (error) => reject(error);
  //   });
  // };

  async function getS3Url(fileName) {
    try {
      const res = await fetch(
        `https://s5a26rvza4jr333q7wugsfkega0gurkl.lambda-url.eu-west-2.on.aws`,
        {
          headers: { "Content-Type": "application/json" },
          method: "POST",
          body: JSON.stringify({
            filename: fileName,
          }),
        }
      );
      const resultURL = await res.json();
      return resultURL;
    } catch (err) {
      console.log("Some Error occured while fetching the s3 URL's", err);
    }
  }

  return (
    <MenuControlsContainer>
      <MenuSelectFontFamily
        options={[
          { label: "Comic Sans", value: "Comic Sans MS, Comic Sans" },
          { label: "Cursive", value: "cursive" },
          { label: "Monospace", value: "monospace" },
          { label: "Serif", value: "serif" },
        ]}
      />

      <MenuDivider />

      <CustomMenuSelectHeading editor={editor} />

      <MenuDivider />

      <MenuSelectFontSize />

      <MenuDivider />

      <MenuButtonBold />

      <MenuButtonItalic />

      <MenuButtonUnderline />

      <MenuButtonStrikethrough />

      <MenuButtonSubscript />

      <MenuButtonSuperscript />

      <MenuDivider />

      <MenuButtonTextColor
        defaultTextColor={theme.palette.text.primary}
        swatchColors={[
          { value: "#000000", label: "Black" },
          { value: "#ffffff", label: "White" },
          { value: "#888888", label: "Grey" },
          { value: "#ff0000", label: "Red" },
          { value: "#ff9900", label: "Orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#00d000", label: "Green" },
          { value: "#0000ff", label: "Blue" },
        ]}
      />

      <MenuButtonHighlightColor
        swatchColors={[
          { value: "#595959", label: "Dark grey" },
          { value: "#dddddd", label: "Light grey" },
          { value: "#ffa6a6", label: "Light red" },
          { value: "#ffd699", label: "Light orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#99cc99", label: "Light green" },
          { value: "#90c6ff", label: "Light blue" },
          { value: "#8085e9", label: "Light purple" },
        ]}
      />

      <MenuButtonBackgroundColor editor={editor} />

      <MenuButtonInlineFields editor={editor} />

      <MenuDivider />

      <MenuButtonEditLink />

      <MenuDivider />

      <MenuSelectTextAlign />

      <MenuDivider />

      <MenuButtonOrderedList />

      <MenuButtonBulletedList />

      <MenuButtonTaskList />

      {/* On touch devices, we'll show indent/unindent buttons, since they're
      unlikely to have a keyboard that will allow for using Tab/Shift+Tab. These
      buttons probably aren't necessary for keyboard users and would add extra
      clutter. */}
      {isTouchDevice() && (
        <>
          <MenuButtonIndent />

          <MenuButtonUnindent />
        </>
      )}

      <MenuDivider />

      <MenuButtonBlockquote />

      <MenuDivider />

      <MenuButtonCode />

      <MenuButtonCodeBlock />

      <MenuDivider />

      <MenuButtonImageUpload
        onUploadFiles={async (files): Promise<any[]> => {
          const uploadPromises = files.map(async (file): Promise<any> => {
            const { uploadUrl, downloadUrl } = await getS3Url(file.name);
            const formData = new FormData();
            formData.append("file", file);
            const resUpload = await fetch(uploadUrl, {
              method: "PUT",
              headers: { "Content-Type": file.type },
              body: file,
            });
            debugger;
            if (resUpload.ok) {
              console.log("Image uploaded successfully to S3!");
              // Use downloadUrl to update editor content or display image (optional)
            } else {
              console.error("Error uploading image:", resUpload.statusText);
            }

            return {
              src: downloadUrl,
              alt: file.name,
            };
          });

          const images = await Promise.all(uploadPromises);
          return images;
        }}
      />

      <MenuDivider />

      <MenuButtonHorizontalRule />

      <MenuButtonAddTable />

      <MenuDivider />

      <MenuButtonRemoveFormatting />

      <MenuDivider />

      <MenuButtonUndo />
      <MenuButtonRedo />
      <MenuButton
        value="tracking"
        tooltipLabel={
          isTrackingEnabled ? "Disable Tracking" : "Enable Tracking"
        }
        size="small"
        onClick={() => setIsTrackingEnabled((prev) => !prev)}
        selected={isTrackingEnabled}
        IconComponent={isTrackingEnabled ? Visibility : VisibilityOff}
        sx={{
          height: "28px",
          minWidth: "28px",
          padding: "4px",
          backgroundColor: "white",
          borderColor: "grey.300",
          color: "grey.600",
          "&:hover": {
            backgroundColor: "grey.100",
            borderColor: "grey.400",
          },
        }}
      />
      <MenuButton
        value="showHistory"
        tooltipLabel={
          isTrackingHistoryEnabled ? "Disable Track Changes History " : "Enable Track Changes History"
        }
        size="small"
        onClick={() => setIsTrackingHistoryEnabled((prev) => !prev)}
        selected={isTrackingHistoryEnabled}
        IconComponent={isTrackingHistoryEnabled ? HistoryIcon : HistoryToggleOffIcon}
        sx={{
          height: "28px",
          minWidth: "28px",
          padding: "4px",
          backgroundColor: "white",
          borderColor: "grey.300",
          color: "grey.600",
          "&:hover": {
            backgroundColor: "grey.100",
            borderColor: "grey.400",
          },
        }}
      />
    </MenuControlsContainer>
  );
}
